import { useEffect, useRef } from 'react';
import type { ModuleId } from '../../contexts/ModuleContext';

/**
 * Landing-page chunk imports for each internal module.
 *
 * Each entry is a factory that triggers the dynamic import() for the module's
 * primary landing page. Calling the factory fetches the JS chunk and caches it
 * in the browser — the component is NOT rendered. When the user later switches
 * to the module, React.lazy picks up the already-cached chunk instantly.
 *
 * External modules (TRAINING, RUDY) are excluded — they open in new tabs.
 */
const MODULE_CHUNK_MAP: Partial<Record<ModuleId, () => Promise<unknown>>> = {
  P1:  () => import('../../pages/CRM/LeadList'),
  P2:  () => import('../../pages/Quotes/QuoteList'),
  P4:  () => import('../../components/Collections/Dashboard/CollectionsDashboardV2'),
  P5:  () => import('../../pages/Support/SupportInbox'),
  P6:  () => import('../../pages/DataHub/ExecutiveDashboard'),
  P7:  () => import('../../pages/Fleet/FleetDashboard'),
  P8:  () => import('../../pages/Forms/FormsDashboard'),
  P9:  () => import('../../pages/HR/HRDashboard'),
  P10: () => import('../../pages/Projects/ProjectsDashboard'),
  P11: () => import('../../pages/FieldService/TicketsPage'),
  P12: () => import('../../pages/Technician/TechToday'),
  P13: () => import('../../pages/FieldService/TeamTimeDashboard'),
  P14: () => import('../../pages/Recruiting/CandidateList'),
  P15: () => import('../../pages/Marketing/MarketingDashboardNew'),
  P16: () => import('../../pages/Huddle/HuddleLanding'),
  P17: () => import('../../pages/Safety/SafetyDashboard'),
  P18: () => import('../../pages/InvestorRelations/Admin/AdminDashboardPage'),
  P19: () => import('../../pages/Workshop/WorkshopDashboard'),
  AI_SYSTEMS: () => import('../../pages/AISystems/Dashboard'),
};

/**
 * Prefetch JS chunks for all modules the user has access to.
 *
 * Runs once after auth, with a short delay to avoid competing with the
 * initial page render. Each import() is fire-and-forget — failures are
 * silently ignored (the chunk will load normally when the user navigates).
 */
export function useModuleChunkPrefetch(
  accessibleModuleIds: readonly ModuleId[],
) {
  const prefetchedIds = useRef(new Set<ModuleId>());

  useEffect(() => {
    const newIds = accessibleModuleIds.filter(
      (id) => !prefetchedIds.current.has(id),
    );
    if (newIds.length === 0) return;

    // Delay prefetch so it doesn't compete with the initial page render
    const timer = window.setTimeout(() => {
      for (const moduleId of newIds) {
        const chunkFactory = MODULE_CHUNK_MAP[moduleId];
        if (chunkFactory) {
          prefetchedIds.current.add(moduleId);
          chunkFactory().catch(() => {
            // Silently ignore — chunk will load on demand
          });
        }
      }
    }, 2000);

    return () => window.clearTimeout(timer);
  }, [accessibleModuleIds]);
}
